import { z } from 'zod';
import { LEGAL_BODY_HTML_MAX_CHARS } from '../content/legalBodyHtml';
import {
  LayoutComponentTypeSchema,
  LocalBusinessAddressSchema,
  OpeningHoursSpecificationSchema,
} from '../content/studio';

/** Locked crawl providers — Firecrawl is the sole paid fallback. Default env: `diy`. */
export const SITE_IMPORT_CRAWL_PROVIDERS = ['diy', 'firecrawl', 'auto'] as const;
export type SiteImportCrawlProviderMode = (typeof SITE_IMPORT_CRAWL_PROVIDERS)[number];

export const ASSIST_RUN_STATUSES = [
  'queued',
  'crawling',
  'mapping',
  'succeeded',
  'failed',
] as const;
export type AssistRunStatus = (typeof ASSIST_RUN_STATUSES)[number];

export const ASSIST_PROPOSAL_LAYERS = ['cdl', 'pdl', 'odl'] as const;
export type AssistProposalLayer = (typeof ASSIST_PROPOSAL_LAYERS)[number];

export const ASSIST_PROPOSAL_STATUSES = ['proposed', 'committed', 'discarded'] as const;
export type AssistProposalStatus = (typeof ASSIST_PROPOSAL_STATUSES)[number];

export const ASSIST_PROPOSAL_KINDS = [
  'cdl.text',
  'pdl.telephone',
  'pdl.address',
  'pdl.openingHours',
  'pdl.legalName',
  'pdl.url',
  'odl.menu',
] as const;
export type AssistProposalKind = (typeof ASSIST_PROPOSAL_KINDS)[number];

export const AssistProposalKindSchema = z.enum(ASSIST_PROPOSAL_KINDS);

const confidenceSchema = z.number().min(0).max(1);
const sourceExcerptSchema = z.string().max(2000).optional();
const sourceUrlSchema = z.string().url().optional();

export const CdlTextProposalPayloadSchema = z.object({
  blockId: z.string().uuid(),
  componentType: LayoutComponentTypeSchema,
  fieldKey: z.string().min(1),
  locale: z.string().min(2).max(16),
  text: z.string().min(1).max(LEGAL_BODY_HTML_MAX_CHARS),
  sourceExcerpt: sourceExcerptSchema,
  sourceUrl: sourceUrlSchema,
  confidence: confidenceSchema.optional(),
});

export const PdlTelephoneProposalPayloadSchema = z.object({
  telephone: z.string().min(3).max(32),
  sourceExcerpt: sourceExcerptSchema,
  sourceUrl: sourceUrlSchema,
  confidence: confidenceSchema.optional(),
});

export const PdlAddressProposalPayloadSchema = z.object({
  address: LocalBusinessAddressSchema,
  sourceExcerpt: sourceExcerptSchema,
  sourceUrl: sourceUrlSchema,
  confidence: confidenceSchema.optional(),
});

export const PdlOpeningHoursProposalPayloadSchema = z.object({
  openingHours: z.array(OpeningHoursSpecificationSchema).min(1),
  sourceExcerpt: sourceExcerptSchema,
  sourceUrl: sourceUrlSchema,
  confidence: confidenceSchema.optional(),
});

export const PdlLegalNameProposalPayloadSchema = z.object({
  legalName: z.string().min(2).max(100),
  sourceExcerpt: sourceExcerptSchema,
  sourceUrl: sourceUrlSchema,
  confidence: confidenceSchema.optional(),
});

export const PdlUrlProposalPayloadSchema = z.object({
  url: z.string().url(),
  sourceExcerpt: sourceExcerptSchema,
  sourceUrl: sourceUrlSchema,
  confidence: confidenceSchema.optional(),
});

/**
 * Handoff: crawl found a PDF/image Speisekarte — Apply opens Menu Import UI
 * (no extract during site-import mapping).
 */
export const OdlMenuHandoffSchema = z.object({
  kind: z.enum(['pdf', 'image']),
  url: z.string().url(),
  label: z.string().min(1).max(200).optional(),
});

export type OdlMenuHandoff = z.infer<typeof OdlMenuHandoffSchema>;

const OdlMenuCategorySchema = z.object({
  name: z.string().min(1).max(200),
  items: z
    .array(
      z.object({
        /** Guest order code when known (e.g. `2a`, `15`). */
        code: z.string().min(1).max(32).optional(),
        name: z.string().min(1).max(200),
        description: z.string().max(2000).optional(),
        price: z.string().max(64).optional(),
      }),
    )
    .max(200),
});

/**
 * Speisekarte proposal for Ask → Import Menu Handoff (never Direct SoT write from Ask).
 * - PDF/image: `handoff` set; categories empty until Import extract.
 * - HTML DIY: categories set (drawer Review prefill); no `handoff`.
 */
export const OdlMenuProposalPayloadSchema = z
  .object({
    mode: z.enum(['merge', 'replace']).default('replace'),
    catalogName: z.string().min(1).max(200).optional(),
    categories: z.array(OdlMenuCategorySchema).max(40).default([]),
    handoff: OdlMenuHandoffSchema.optional(),
    sourceExcerpt: sourceExcerptSchema,
    sourceUrl: sourceUrlSchema,
    confidence: confidenceSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.handoff) return;
    if (value.categories.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'HTML odl.menu Handoff requires at least one mapped category.',
        path: ['categories'],
      });
    }
  });

export type OdlMenuProposalPayload = z.infer<typeof OdlMenuProposalPayloadSchema>;

/** True when payload is PDF/image media Handoff (vs HTML DIY categories). */
export function isOdlMenuHandoffPayload(
  payload: unknown,
): payload is OdlMenuProposalPayload & { handoff: OdlMenuHandoff } {
  const parsed = OdlMenuProposalPayloadSchema.safeParse(payload);
  return Boolean(parsed.success && parsed.data.handoff);
}

export const AssistProposalPayloadSchema = z.union([
  CdlTextProposalPayloadSchema,
  PdlTelephoneProposalPayloadSchema,
  PdlAddressProposalPayloadSchema,
  PdlOpeningHoursProposalPayloadSchema,
  PdlLegalNameProposalPayloadSchema,
  PdlUrlProposalPayloadSchema,
  OdlMenuProposalPayloadSchema,
]);

export type AssistProposalPayload = z.infer<typeof AssistProposalPayloadSchema>;

export const CrawlPageSchema = z.object({
  url: z.string().url(),
  finalUrl: z.string().url(),
  title: z.string().max(500).optional(),
  html: z.string().max(2_000_000).optional(),
  text: z.string().max(200_000).optional(),
  markdown: z.string().max(200_000).optional(),
  jsonLd: z.array(z.unknown()).optional(),
  httpStatus: z.number().int().optional(),
});

export type CrawlPage = z.infer<typeof CrawlPageSchema>;

export const CrawlPackSchema = z.object({
  rootUrl: z.string().url(),
  provider: z.enum(['diy', 'firecrawl']),
  pages: z.array(CrawlPageSchema).max(8),
  warnings: z.array(z.string()).default([]),
});

export type CrawlPack = z.infer<typeof CrawlPackSchema>;
